import { BannerGroup } from '@quickmd-llc/design-system'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserRoles } from 'hooks/useUserRoles'
import { installIterableActionHandlers, setIterableNavigationContext } from 'lib/iterable/actions'
import { endSession, pauseImpression, runButtonAction, startImpression, startSession } from 'lib/iterable/embedded'
import type { RenderableMessage } from 'lib/iterable/types'
import { useIsIterableIdentified, useIterableEmbeddedMessages } from 'lib/iterable/useIterableEmbeddedMessages'
import { selectIterableAnnouncementsEnabled } from 'store/flags/FlagsSelectors'
import { useSelector } from 'store/hooks/useSelector'
import env from 'env.json'
import { AnnouncementBanner } from './AnnouncementBanner'

/**
 * The dashboard announcement placement: every embedded message Iterable is currently serving for
 * this placement, stacked above the visit list (QMD-4694).
 *
 * **Plural by design.** A placement returns a list, and two campaigns can target the same
 * placement at once, so this maps over whatever comes back rather than reaching for `[0]`. Two or
 * more are drawn as one card with a divider between rows, in the order Iterable returned them
 * (Figma 52306:154818); a lone message is a plain banner.
 *
 * Renders `null` — not an empty wrapper — when there is nothing to show, which is both the
 * explicit AC and what keeps the dashboard's `gap-sp-2` column from growing a blank row.
 */
export function AnnouncementPlacement() {
  const isEnabled = useSelector(selectIterableAnnouncementsEnabled)
  const isIdentified = useIsIterableIdentified()
  const { messages, dismiss } = useIterableEmbeddedMessages(env.iterableDashboardPlacementId, isEnabled, isIdentified)

  const navigate = useNavigate()
  const roles = useUserRoles()
  useEffect(() => {
    installIterableActionHandlers()
    setIterableNavigationContext({ navigate: (path) => navigate(path), roles })
  }, [navigate, roles])

  // An Iterable "session" is the window in which impressions accumulate; they are only sent when
  // it ends. So this ends the session on `pagehide` as well as on unmount — a provider closing
  // the tab is the ordinary way out of the dashboard, and React cleanup does not run for it.
  // `pagehide` rather than `beforeunload` because the latter is skipped for bfcache restores.
  useEffect(() => {
    if (!isEnabled) return undefined
    startSession()
    window.addEventListener('pagehide', endSession)
    return () => {
      window.removeEventListener('pagehide', endSession)
      endSession()
    }
  }, [isEnabled])

  if (!isEnabled || messages.length === 0) return null

  return (
    <BannerGroup variant='announcement'>
      {messages.map((message) => (
        <AnnouncementRow
          key={message.metadata.messageId}
          message={message}
          onDismiss={(buttonIdentifier) => dismiss(message, buttonIdentifier)}
        />
      ))}
    </BannerGroup>
  )
}

type RowProps = {
  message: RenderableMessage
  onDismiss: (buttonIdentifier: string) => void
}

/**
 * One announcement plus its impression tracking.
 *
 * Impressions are per message and depend on whether that message is actually on screen, so the
 * observer lives with the row rather than the list — a provider who never scrolls past the first
 * announcement should not be counted as having seen the third.
 */
function AnnouncementRow({ message, onDismiss }: RowProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const { messageId, placementId } = message.metadata

  useEffect(() => {
    const element = ref.current
    if (!element) return undefined

    // No placement id means there is nothing to attribute the impression to. Reporting it under
    // a stand-in (0) would file real views against a placement that does not exist, which is
    // worse than not counting them — this feature's whole output is the Snowflake numbers.
    if (placementId === undefined) return undefined

    // Only a crossing of the 0.5 line changes anything. `isIntersecting` alone is true at any
    // overlap, so a row scrolling from fully shown to 40% shown would read as "visible" again, and
    // a second `startImpression` resets the SDK's open interval, dropping the time already seen.
    // One callback can carry several entries for the same row; the last one is the current state.
    let isCounting = false
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.at(-1)
        if (!entry) return
        const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5
        if (isVisible === isCounting) return
        isCounting = isVisible
        if (isVisible) startImpression(messageId, placementId)
        else pauseImpression(messageId)
      },
      { threshold: 0.5 },
    )
    observer.observe(element)
    return () => {
      // Pause before disconnecting: unmount (a dismiss, or leaving the dashboard) otherwise ends
      // the impression without ever banking its duration.
      pauseImpression(messageId)
      observer.disconnect()
    }
  }, [messageId, placementId])

  return (
    <div ref={ref}>
      <AnnouncementBanner
        message={message}
        onDismiss={onDismiss}
        onAction={(buttonIdentifier, targetUrl) => runButtonAction(message, buttonIdentifier, targetUrl)}
      />
    </div>
  )
}
